// src/components/InseeAutocomplete.jsx
import React from "react";
import axiosInstance from "../axiosInstance";

const useDebounced = (value, delay = 300) => {
    const [v, setV] = React.useState(value);
    React.useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

export default function InseeAutocomplete({
    value,
    onChange,
    onSelect,
    placeholder = "Rechercher une société…",
    minChars = 3,
}) {
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [items, setItems] = React.useState([]);
    const [highlight, setHighlight] = React.useState(-1);
    const [err, setErr] = React.useState("");

    const debounced = useDebounced(value, 250);
    const boxRef = React.useRef(null);

    React.useEffect(() => {
        const onDoc = (e) => {
            if (!boxRef.current) return;
            if (!boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    React.useEffect(() => {
        const q = String(debounced || "").trim();
        setErr("");
        setItems([]);

        // SIRET direct
        if (/^\d{14}$/.test(q)) {
            (async () => {
                try {
                    setLoading(true);
                    const { data } = await axiosInstance.get(`/entreprise-ext/by-siret/${q}`);
                    const arr = data ? [data] : [];
                    const clean = arr.filter(it => it && (it.nom || it.enseigne));
                    setItems(clean);
                    setOpen(true);
                    setHighlight(clean.length ? 0 : -1);
                } catch (e) {
                    setErr("Aucun établissement trouvé pour ce SIRET.");
                    setOpen(true);
                } finally {
                    setLoading(false);
                }
            })();
            return;
        }

        if (q.length < Math.max(2, minChars)) {
            setOpen(false);
            return;
        }

        (async () => {
            try {
                setLoading(true);
                const { data } = await axiosInstance.get(`/entreprise-ext/search`, { params: { q } });
                // sécurité: enlève les lignes sans nom
                const clean = (Array.isArray(data) ? data : []).filter(it => it && (it.nom || it.enseigne));
                setItems(clean);
                setOpen(true);
                setHighlight(-1);
            } catch (e) {
                setErr("Erreur de recherche.");
                setOpen(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [debounced, minChars]);

    const choose = (it) => {
        setOpen(false);
        setHighlight(-1);
        if (it?.nom) onChange?.(it.nom);
        onSelect?.(it);
    };

    const onKeyDown = (e) => {
        if (!open || !items.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(items.length - 1, h + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === "Enter") {
            if (highlight >= 0 && items[highlight]) {
                e.preventDefault();
                choose(items[highlight]);
            }
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={boxRef}>
            <input
                className="border rounded-lg px-3 py-2 w-full"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                onFocus={() => value?.length >= minChars && setOpen(true)}
                onKeyDown={onKeyDown}
                autoComplete="off"
            />

            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-xl border bg-white shadow-lg overflow-hidden max-h-80 overflow-y-auto">
                    {loading && <div className="px-3 py-2 text-sm text-slate-500">Recherche…</div>}

                    {!loading && err && <div className="px-3 py-2 text-sm text-rose-600">{err}</div>}

                    {!loading && !err && items.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500">Aucun résultat.</div>
                    )}

                    {!loading &&
                        items.map((it, idx) => {
                            // Affichages robustes
                            const name =
                                it.nom ||
                                it.label ||
                                it.nom_complet ||
                                it.denomination ||
                                "—";
                            const addr = it.adresse || {};
                            const line2 = [addr.zip, addr.city].filter(Boolean).join(" ");
                            return (
                                <button
                                    key={`${it.siret || it.siren || name || idx}`}
                                    type="button"
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${idx === highlight ? "bg-slate-50" : ""
                                        }`}
                                    onMouseEnter={() => setHighlight(idx)}
                                    onClick={() => choose(it)}
                                >
                                    <div className="font-medium">{name}</div>
                                    {(addr.line1 || line2) && (
                                        <div className="text-[12px] text-slate-600">
                                            {addr.line1 || ""} {line2 ? `• ${line2}` : ""}
                                        </div>
                                    )}
                                    <div className="text-[12px] text-slate-500">
                                        {it.siret ? `SIRET ${it.siret}` : it.siren ? `SIREN ${it.siren}` : ""}
                                        {it.code_naf ? ` • APE ${it.code_naf}` : ""}
                                        {it.forme_juridique ? ` • ${it.forme_juridique}` : ""}
                                    </div>
                                </button>
                            );
                        })}
                </div>
            )}
        </div>
    );
}
